import { Link } from "@tanstack/react-router";
import { Columns2, Columns4, Pencil } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ErrorState } from "@/components/shared/error-state";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import type { RoleLevelOption } from "@/hooks/use-role-level-applicability";
import {
  type CapabilityExpectations,
  type LevelExpectations,
  useRoleLevelExpectations,
  useSetRoleLevelExpectations,
} from "@/hooks/use-role-level-expectations";
import { originLinkSearch } from "@/lib/detail-origin";
import { cn } from "@/lib/utils";

const LEVEL_BUTTON =
  "inline-flex items-center gap-2 whitespace-nowrap rounded-md border px-3 py-1.5 text-sm font-medium transition-colors";
const LEVEL_ACTIVE = "border-primary bg-primary text-primary-foreground";
const LEVEL_INACTIVE = "border-input bg-card text-foreground hover:bg-muted/50";

/**
 * The level switcher + expectation grid on role detail (WI-105 over WI-107's
 * data layer).
 *
 * Only the levels the role actually uses appear — levels it doesn't use are
 * ABSENT, never greyed out. The viewer's own level is flagged and selected
 * first, so someone opening their own role lands on the rung they're at.
 *
 * Every signed-in user may read all five performance rungs (Matt, 2026-08-05),
 * so the grid opens on the meets rung — the question most people arrive with —
 * and anyone can toggle the full ladder into view.
 *
 * **What you're ALLOWED to see is still the server's decision, not this
 * component's.** This renders whatever rungs arrive and the toggle only chooses
 * between them; if the audience is ever narrowed again, the fix belongs in the
 * route, not here.
 */
export function RoleLevelExpectations({
  roleId,
  roleName,
  levels,
  isAdmin,
}: {
  roleId: string;
  /** Only for the origin breadcrumb on the capability links. */
  roleName: string;
  levels: RoleLevelOption[];
  /** Gates the per-capability edit affordance (WI-111). */
  isAdmin: boolean;
}) {
  const applied = levels.filter((l) => l.applies);
  const [picked, setPicked] = useState<string | null>(null);
  // Off by default: the meets rung is what this page is for. Persists across
  // level switches so an admin comparing rungs doesn't re-toggle each time.
  const [showAllRatings, setShowAllRatings] = useState(false);

  // Default to the viewer's own level, else the lowest rung. Derived rather than
  // effect-seeded so a level disappearing from the role (or the options arriving
  // late) can't leave a dangling selection.
  const fallback = applied.find((l) => l.heldByViewer)?.id ?? applied[0]?.id ?? null;
  const activeId = picked !== null && applied.some((l) => l.id === picked) ? picked : fallback;
  const activeLevel = applied.find((l) => l.id === activeId);

  const { data, isLoading, isError, refetch } = useRoleLevelExpectations(roleId, activeId);

  // No ladder means no grid to write into, which is otherwise a dead end: an
  // admin looking for the expectation editor needs to be told the levels come
  // first, and where to set them.
  if (applied.length === 0) {
    return (
      <section className="rounded-lg border border-dashed bg-card px-4 py-6 text-center">
        <p className="text-sm font-medium">No career levels on this role</p>
        <p className="mx-auto mt-1 max-w-prose text-sm text-muted-foreground">
          {isAdmin
            ? "Expectations are written per career level. Add this role's levels with Edit → Career levels, then each level gets its own expectation grid."
            : "Once this role has career levels, each one will show what's expected at that level."}
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-4 border-b pb-6">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-medium">Expectations by career level</h3>
          {data &&
            (data.ratings.length > 1 ? (
              // Open to everyone, not just admins (Matt, 2026-08-05). Pressed
              // state reads as filled so it's obvious the grid is expanded.
              <Button
                type="button"
                variant={showAllRatings ? "default" : "outline"}
                size="sm"
                aria-pressed={showAllRatings}
                onClick={() => setShowAllRatings((v) => !v)}
              >
                {showAllRatings ? (
                  <>
                    <Columns2 className="size-3.5" />
                    Meets expectations only
                  </>
                ) : (
                  <>
                    <Columns4 className="size-3.5" />
                    Show all performance ratings
                  </>
                )}
              </Button>
            ) : (
              <p className="text-xs text-muted-foreground">
                Showing what {data.ratings[0]?.name.toLowerCase()} looks like.
              </p>
            ))}
        </div>

        <div className="flex flex-wrap gap-2">
          {applied.map((level) => {
            const active = level.id === activeId;
            return (
              <button
                key={level.id}
                type="button"
                aria-pressed={active}
                onClick={() => setPicked(level.id)}
                className={cn(LEVEL_BUTTON, active ? LEVEL_ACTIVE : LEVEL_INACTIVE)}
              >
                {level.name}
                {level.heldByViewer && (
                  <span
                    className={cn(
                      "rounded-sm px-1.5 py-0.5 text-3xs font-semibold uppercase tracking-wide",
                      active ? "bg-primary-foreground/20" : "bg-muted text-muted-foreground",
                    )}
                  >
                    Your level
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {activeLevel?.definition && (
          <p className="text-sm text-muted-foreground">{activeLevel.definition}</p>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full rounded-lg" />
      ) : isError || !data ? (
        <ErrorState
          className="py-4"
          message="This level's expectations couldn't be loaded."
          onRetry={() => refetch()}
        />
      ) : data.capabilities.length === 0 ? (
        <p className="rounded-lg border bg-card px-4 py-6 text-center text-sm text-muted-foreground">
          This role doesn't require any skills or competencies yet. Add some below and expectations
          can be written against them per level.
        </p>
      ) : (
        <ExpectationGrid
          data={data}
          showAllRatings={showAllRatings}
          roleId={roleId}
          roleName={roleName}
          isAdmin={isAdmin}
        />
      )}
    </section>
  );
}

function ExpectationGrid({
  data,
  showAllRatings,
  roleId,
  roleName,
  isAdmin,
}: {
  data: LevelExpectations;
  showAllRatings: boolean;
  roleId: string;
  roleName: string;
  isAdmin: boolean;
}) {
  // Which capability's rungs are open in the editor. Null = closed.
  const [editing, setEditing] = useState<CapabilityExpectations | null>(null);
  // Default to the meets rung alone. The fallback matters: if the meets ordinal
  // somehow isn't in what the server sent, show everything rather than an
  // empty grid.
  const meetsOnly = data.ratings.filter((r) => r.ordinal === data.meetsOrdinal);
  const ratings = showAllRatings || meetsOnly.length === 0 ? data.ratings : meetsOnly;

  // Several rungs make each column narrow, so the table scrolls inside its own
  // container rather than squashing the text.
  const wide = ratings.length > 1;

  return (
    <div className={cn("rounded-lg border", wide && "overflow-x-auto")}>
      <table className="w-full text-sm">
        <caption className="sr-only">
          Each capability this role requires at the {data.roleLevelName} level: its description,
          then what is expected of it at each performance rating.
        </caption>
        <thead>
          <tr className="border-b bg-muted/40">
            <th scope="col" className="w-48 px-3 py-2 text-left font-medium">
              Capability
            </th>
            {/* What the capability IS, read before what any rung on it means.
                Its own column rather than a line under the name so it doesn't
                push the rating cells out of line with the row. Headed
                "Description" to match what the skills and competencies pages
                call the same field. */}
            <th
              scope="col"
              className={cn("w-64 px-3 py-2 text-left font-medium", wide && "min-w-56")}
            >
              Description
            </th>
            {ratings.map((rating) => (
              <th
                key={rating.ordinal}
                scope="col"
                className={cn("px-3 py-2 text-left font-medium", wide && "min-w-56")}
              >
                {rating.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.capabilities.map((cap) => (
            <tr key={`${cap.kind}-${cap.id}`} className="border-b last:border-b-0 align-top">
              <th scope="row" className="px-3 py-2.5 text-left font-medium">
                {/* The grid is the only place these are listed now, so the name
                    has to be the way through to the capability itself. Origin
                    search brings the reader back here, not to the generic list. */}
                {cap.kind === "skill" ? (
                  <Link
                    to="/dashboard/skills/$skillId"
                    params={{ skillId: cap.id }}
                    search={originLinkSearch("role", roleId, roleName)}
                    className="block text-primary hover:underline"
                  >
                    {cap.name}
                  </Link>
                ) : (
                  <Link
                    to="/dashboard/competencies/$competencyId"
                    params={{ competencyId: cap.id }}
                    search={originLinkSearch("role", roleId, roleName)}
                    className="block text-primary hover:underline"
                  >
                    {cap.name}
                  </Link>
                )}
                <span className="block text-xs font-normal capitalize text-muted-foreground">
                  {cap.kind}
                </span>
                {isAdmin && (
                  // Per capability rather than per cell: the editor opens all
                  // five rungs at once, which is how the escalation between them
                  // is actually written — and it means an admin never has to
                  // expand the grid to author the other four.
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 h-7"
                    // One per row, plus the page header's own Edit — the
                    // accessible name has to say which is which.
                    aria-label={`Edit expectations for ${cap.name}`}
                    onClick={() => setEditing(cap)}
                  >
                    <Pencil className="size-3" />
                    Edit expectations
                  </Button>
                )}
              </th>
              <td className="px-3 py-2.5 leading-relaxed text-muted-foreground">
                {cap.description ?? <span className="italic">No description written yet</span>}
              </td>
              {ratings.map((rating) => {
                const text = cap.byOrdinal[rating.ordinal];
                return (
                  <td key={rating.ordinal} className="px-3 py-2.5 leading-relaxed">
                    {text ? (
                      text
                    ) : (
                      <span className="text-muted-foreground italic">Not written yet</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {isAdmin && editing && (
        <ExpectationEditDialog
          open
          onOpenChange={(o) => !o && setEditing(null)}
          roleId={roleId}
          roleLevelId={data.roleLevelId}
          roleLevelName={data.roleLevelName}
          capability={editing}
          /* All five rungs, whatever the grid is currently showing — the
             collapsed view is a reading choice, not an authoring one. */
          ratings={data.ratings}
        />
      )}
    </div>
  );
}

/**
 * Authoring one capability's expectations at one level (WI-111) — every rating
 * rung the session may read, in one form.
 *
 * Only changed rungs are written, so an admin fixing one line doesn't produce
 * five audit rows. A cleared field writes null, which the server treats as
 * "not written yet" rather than an empty string.
 *
 * Closing with unsaved wording asks first. A misclick on the backdrop used to
 * throw away several paragraphs of freshly-typed expectations with no way back,
 * which is the single most expensive mistake this form allows — so every close
 * route (backdrop, Escape, the X, Cancel) funnels through `requestClose`.
 */
function ExpectationEditDialog({
  open,
  onOpenChange,
  roleId,
  roleLevelId,
  roleLevelName,
  capability,
  ratings,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleId: string;
  roleLevelId: string;
  roleLevelName: string;
  capability: CapabilityExpectations;
  ratings: { ordinal: number; name: string }[];
}) {
  const save = useSetRoleLevelExpectations(roleId);
  // Seeded once per open — the dialog is mounted only while open (keyed by the
  // capability), so there's no stale-draft problem to guard against.
  const [draft, setDraft] = useState<Record<number, string>>(() =>
    Object.fromEntries(ratings.map((r) => [r.ordinal, capability.byOrdinal[r.ordinal] ?? ""])),
  );

  const changed = ratings.filter(
    (r) => (draft[r.ordinal] ?? "").trim() !== (capability.byOrdinal[r.ordinal] ?? "").trim(),
  );
  // Shown when a close was attempted with unsaved wording still in the form.
  const [confirmClose, setConfirmClose] = useState(false);

  /**
   * The only way this dialog closes. Radix routes the backdrop, Escape and the
   * X here via `onOpenChange`, and Cancel calls it directly — so the guard can't
   * be sidestepped by whichever one the admin happens to hit.
   */
  function requestClose(next: boolean) {
    if (next) return; // this dialog is only ever opened by mounting it
    if (changed.length > 0 && !save.isPending) {
      setConfirmClose(true);
      return;
    }
    onOpenChange(false);
  }

  async function submit() {
    if (changed.length === 0) {
      onOpenChange(false);
      return;
    }
    try {
      await save.mutateAsync(
        changed.map((r) => ({
          roleLevelId,
          kind: capability.kind,
          capabilityId: capability.id,
          ordinal: r.ordinal,
          expectation: (draft[r.ordinal] ?? "").trim() || null,
        })),
      );
      toast.success(`Saved ${capability.name} at ${roleLevelName}`);
      onOpenChange(false);
    } catch {
      // The mutation toasts the server's message; keep the dialog open so the
      // admin's wording isn't thrown away.
    }
  }

  return (
    <Dialog open={open} onOpenChange={requestClose}>
      {/* Grows with the window: five rungs of prose in a 42rem-capped dialog
          meant scrolling a stack of postage stamps on a wide screen. */}
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl lg:max-w-4xl xl:max-w-5xl">
        <DialogHeader>
          <DialogTitle>{capability.name}</DialogTitle>
          <DialogDescription>
            What this {capability.kind} looks like at each performance rating for the{" "}
            {roleLevelName} level of this role. Leave a rating blank if it isn't written yet.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {ratings.map((rating) => (
            <div key={rating.ordinal} className="space-y-1.5">
              <Label htmlFor={`expectation-${rating.ordinal}`}>{rating.name}</Label>
              <Textarea
                id={`expectation-${rating.ordinal}`}
                rows={3}
                maxLength={2000}
                value={draft[rating.ordinal] ?? ""}
                onChange={(e) =>
                  setDraft((prev) => ({ ...prev, [rating.ordinal]: e.target.value }))
                }
                placeholder="Not written yet"
              />
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => requestClose(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={save.isPending || changed.length === 0}>
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>

      {confirmClose && (
        <AlertDialog open onOpenChange={() => setConfirmClose(false)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Save your changes?</AlertDialogTitle>
              <AlertDialogDescription>
                {changed.length === 1
                  ? `You've written something under “${changed[0]?.name}” for ${capability.name} and haven't saved it.`
                  : `You've changed ${changed.length} ratings for ${capability.name} and haven't saved them.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              {/* Cancel = "I didn't mean to close" — the safe default the
                  Escape key and the backdrop both land on. */}
              <AlertDialogCancel>Keep editing</AlertDialogCancel>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setConfirmClose(false);
                  onOpenChange(false);
                }}
              >
                Discard
              </Button>
              <AlertDialogAction
                onClick={() => {
                  setConfirmClose(false);
                  void submit();
                }}
              >
                Save
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </Dialog>
  );
}
